import React, { useState } from "react";
import { ShieldCheck, Search, CheckCircle2, XCircle, Loader2, Download } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Verify() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async () => {
    if (!q.trim()) return;
    setLoading(true); setRes(null);
    try { const { data } = await api.get(`/public/verify-certificate?q=${encodeURIComponent(q.trim())}`); setRes(data); }
    catch (e) { setRes({ found: false }); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] bg-[#f7faf8] py-16 px-5">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#004030] text-[#e0c05a] grid place-items-center mx-auto mb-4"><ShieldCheck size={26} /></div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#012a1e]">Certificate Verification</h1>
          <p className="text-slate-500 mt-2">Verify a GDIH Elite Education certificate. Enter the certificate number or the student reference code.</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <div className="flex gap-2">
            <Input data-testid="verify-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && check()}
              placeholder="e.g. GDIH-CERT-00123 or GDIH-MY-000101" className="h-12" />
            <Button data-testid="verify-btn" onClick={check} disabled={loading} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-6 h-12">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
            </Button>
          </div>

          {res && (res.found ? (
            <div data-testid="verify-result-valid" className="mt-6 rounded-xl border border-green-200 bg-green-50 p-5">
              <div className="flex items-center gap-2 mb-3">
                {res.status === "Revoked"
                  ? <><XCircle className="text-red-600" size={22} /><span className="font-bold text-red-700">Certificate REVOKED</span></>
                  : <><CheckCircle2 className="text-green-600" size={22} /><span className="font-bold text-green-700">Verified — Valid Certificate</span></>}
              </div>
              <dl className="text-sm space-y-1.5">
                <div className="flex justify-between"><dt className="text-slate-500">Certificate No.</dt><dd className="font-semibold text-[#012a1e]">{res.certificate_no || "—"}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Student</dt><dd className="font-semibold text-[#012a1e]">{res.student_name}</dd></div>
                {res.student_code && <div className="flex justify-between"><dt className="text-slate-500">Reference code</dt><dd className="font-mono text-[#004030]">{res.student_code}</dd></div>}
                <div className="flex justify-between"><dt className="text-slate-500">Programme</dt><dd className="font-semibold text-[#012a1e] text-right">{res.programme}</dd></div>
                {res.institution && <div className="flex justify-between"><dt className="text-slate-500">Institution</dt><dd className="text-[#012a1e] text-right">{res.institution}</dd></div>}
                {res.grade && <div className="flex justify-between"><dt className="text-slate-500">Grade / Result</dt><dd className="text-[#012a1e]">{res.grade}</dd></div>}
                {res.issue_date && <div className="flex justify-between"><dt className="text-slate-500">Issued</dt><dd className="text-[#012a1e]">{res.issue_date}</dd></div>}
              </dl>
              {res.status !== "Revoked" && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/certificate-pdf?q=${encodeURIComponent(res.certificate_no || q.trim())}`} target="_blank" rel="noreferrer" data-testid="verify-download-pdf">
                  <Button className="mt-4 w-full bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-11"><Download size={16} className="mr-1" /> Download Certificate PDF</Button>
                </a>
              )}
            </div>
          ) : (
            <div data-testid="verify-result-none" className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 flex items-center gap-2">
              <XCircle className="text-red-600" size={22} /><span className="text-red-700 font-medium">No matching certificate found. Please check the number and try again.</span>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">For queries, contact GDIH Elite Education. This tool confirms records issued by GDIH.</p>
      </div>
    </div>
  );
}
